# Types

Complete TypeScript type definitions for Taias. Most types are inferred automatically - reference this page when you need explicit type annotations or are building advanced integrations.

---

## Core Types

Types you'll use when defining flows and integrating Taias.

```ts
// Context passed to resolve()
type TaiasContext = {
  toolName: string;
  params?: Record<string, unknown>; // Tool input parameters
  result?: Record<string, unknown>; // Tool execution output
};

// What a logic statement produces
type StepDecision = {
  nextTool: string;
  [key: string]: string; // Any number of decision fields
};

// Generalized decision object (one or more fields defined by the logic statement)
type Decision = Record<string, string | undefined>;
```

### Decision

The Decision is Taias's core intermediate representation - the output of flow logic before it's manifested into advice or UI selections.

```ts
// What you'll see at runtime:
// { nextTool: "get_recommendations", secondaryAction: "create_playlist" }

// The TypeScript type (intentionally extensible):
type Decision = Record<string, string | undefined>;
```

**Why `Record<string, string | undefined>`?**

The Decision type is intentionally general -- it contains one or more fields defined by your logic statement. This structure supports multi-dimensional control: the LLM advice layer and each UI slot can each use different decision fields. When you access any field on the decision object, TypeScript allows it because of the index signature.

**Accessing the decision directly:**

```ts
const { decision } = await taias.resolve({ toolName: "scan_repo" });

// Use the decision for custom logic
if (decision.nextTool === "configure_app") {
  // Custom handling
}
```

Most integrations won't need to inspect `decision` directly - the `advice` and `selections` manifestations are usually sufficient. But having access to the raw decision enables advanced use cases.

### Affordances

```ts
// Complete output from resolve() (generic over slot type)
type Affordances<S extends string = DefaultSlots> = {
  advice: string; // LLM guidance text
  decision: Decision; // The flow decision
  selections: UiSelections<S>; // UI affordance selections
};
```

---

## Flow Definition

Types related to building flows.

```ts
// Handler function for a flow step (escape hatch for dynamic decisions)
type StepHandler = (
  ctx: TaiasContext,
) => StepDecision | null | Promise<StepDecision | null>;

// The input accepted by flow.step() -- either a static decision or a handler function
type StepInput = StepHandler | StepDecision;

// Complete flow definition returned by defineFlow()
type FlowDefinition = {
  id: string;
  steps: Array<FlowStep>;
};

// Builder API for registering steps
interface FlowBuilder {
  step(match: MatchCondition, input: StepInput): void;
}
```

`step()` takes two arguments: a **match condition** object (the conditions under which this step applies) and a **decision** object (what to produce when matched). Each field in the match condition uses an explicit `Condition` operator (`{ is: ... }` or `{ isNot: ... }`). All fields are optional -- steps can match on any combination of `toolName`, `params`, and `result`. A `StepHandler` function is also accepted in place of a decision object for backwards compatibility.

---

## Operator Types

Types for the match condition operator system.

```ts
// A comparison operator applied to a single field value.
// Pure data -- Taias interprets these, they are not wrapper functions.
// Values are `unknown`, enabling matching on strings, numbers, booleans, etc.
type Condition =
  | { is: unknown } // exact equality (field === value)
  | { isNot: unknown }; // negation       (field !== value)
```

### Condition

`Condition` is a union of operator objects. Each variant specifies a comparison to apply to a field value using strict equality (`===`):

- `{ is: value }` -- the field must equal `value`
- `{ isNot: value }` -- the field must not equal `value`

Values are typed as `unknown`, enabling matching on any value type: strings, numbers, booleans, and more. Operators are pure data, aligning with the logic-as-data philosophy. New operators (e.g., `oneOf`, `contains`) can be added as union members without changing the evaluation architecture.

---

## Logic Statement Types

Types for the declarative logic statement system.

```ts
// Match condition for a logic statement.
// All fields are optional -- steps can match on any combination.
// Each field uses explicit Condition operators.
type MatchCondition = {
  toolName?: Condition;
  params?: Record<string, Condition>;
  result?: Record<string, Condition>;
};

// A declarative logic statement -- the core primitive of the decision engine
// Formalizes "Given X, then Y" logic into structured data
type LogicStatement = {
  match: MatchCondition;
  decision: StepDecision;
};

// A step within a flow -- discriminated union
type FlowStep =
  | { kind: "logic"; statement: LogicStatement }
  | { kind: "handler"; match: MatchCondition; handler: StepHandler };
```

### MatchCondition

`MatchCondition` describes the conditions under which a step applies. All fields are optional:

- `toolName?: Condition` -- match against the tool name in the context
- `params?: Record<string, Condition>` -- match against specific keys in the tool's input parameters (subset matching: only specified keys are checked)
- `result?: Record<string, Condition>` -- match against specific keys in the tool's execution output (subset matching: only specified keys are checked)

Each field uses an explicit `Condition` operator (`{ is: ... }` or `{ isNot: ... }`). If a step specifies `params` or `result` conditions but the context doesn't include them, the step does not match.

### FlowStep

`FlowStep` is a discriminated union. The primary variant is **logic steps**:

**Logic steps** (`kind: "logic"`): The `statement` is the sole source of truth for the step's match conditions and decision. The decision is returned directly at resolve time with no function invocation.

> **Backwards compatibility:** Handler steps (`kind: "handler"`) are also supported. The `match` condition is stored alongside a `handler` function, which is called with the full `TaiasContext` at resolve time.

---

## Observability Types

Types for the event emitter and resolve tracing system.

```ts
// Structured event emitted on every resolve() call
type ResolveEvent<S extends string = DefaultSlots> = {
  flowId: string;
  timestamp: number;
  durationMs: number;
  context: TaiasContext;
  trace: ResolveTrace;
  decision: Decision | null;
  affordances: {
    advice: string;
    selections: UiSelections<S>;
  } | null;
};

// Trace of how resolve() reached its decision
type ResolveTrace = {
  matched: boolean;
  matchedStepIndex: number | null;
  matchedStepKind: "logic" | "handler" | null;
  matchedStepMatch: MatchCondition | null;
  phase: "indexed" | "broad" | null;
  resolutionPath: string[];
  candidatesEvaluated: number;
  evaluations?: StepEvaluation[]; // Present when tracing: "detailed"
};

// Per-step evaluation record (detailed tracing only)
type StepEvaluation = {
  stepIndex: number;
  match: MatchCondition;
  result: "matched" | "no-match";
  fieldResults?: Record<
    string,
    {
      condition: Condition;
      actual: unknown;
      passed: boolean;
    }
  >;
};

// Map of event names to their event types
type TaiasEventMap<S extends string = DefaultSlots> = {
  resolve: ResolveEvent<S>;
};
```

### ResolveEvent

The four-part event emitted after every `resolve()` call:

- `context` -- what was passed to `resolve()` (the `TaiasContext`)
- `trace` -- how the decision was reached (see `ResolveTrace` below)
- `decision` -- the raw decision object, or `null` if no match or handler returned `null`
- `affordances` -- the manifested advice and UI selections, or `null`

### ResolveTrace

The summary trace is always present and captures:

- `matched` -- whether any step matched the context
- `matchedStepIndex` -- the index of the matched step in the flow's steps array
- `matchedStepKind` -- `"logic"` or `"handler"`, indicating the step type
- `matchedStepMatch` -- the full `MatchCondition` of the matched step
- `phase` -- `"indexed"` (found via per-field index lookup) or `"broad"` (found via linear scan of non-indexable steps)
- `resolutionPath` -- which field indexes were consulted (e.g., `["toolName", "params.language"]`)
- `candidatesEvaluated` -- how many steps were evaluated before a match was found (or all candidates exhausted)

When `tracing: "detailed"` is enabled, the `evaluations` array is also populated with per-step breakdowns. Each `StepEvaluation` records the step index, its match condition, the evaluation result, and optionally per-field outcomes showing what was expected vs. what the context contained.

### TaiasEventMap

Maps event names to their payload types. Currently contains only `resolve: ResolveEvent<S>`. Extensible to future event types (e.g., `"init"`, `"error"`).

---

## Instance Creation

Types for creating and configuring Taias instances. Generic over slot type `S` for custom slot support.

```ts
// Options for createTaias() (generic over slot type)
type TaiasOptions<S extends string = DefaultSlots> = {
  flow: FlowDefinition;
  affordances?: AffordanceRegistry<S>; // UI affordance registry
  devMode?: boolean;
  debug?: boolean | DebugOptions; // Enable built-in debug output
  tracing?: "summary" | "detailed"; // Trace level (default: "summary")
  onMissingStep?: (ctx: TaiasContext) => void;
  onWarn?: (msg: string) => void; // Custom warning handler
};

// Taias instance with resolve and event emitter methods (generic over slot type)
interface Taias<S extends string = DefaultSlots> {
  resolve(
    ctx: TaiasContext,
  ): Affordances<S> | null | Promise<Affordances<S> | null>;
  on<E extends keyof TaiasEventMap<S>>(
    event: E,
    handler: (data: TaiasEventMap<S>[E]) => void,
  ): void;
  off<E extends keyof TaiasEventMap<S>>(
    event: E,
    handler: (data: TaiasEventMap<S>[E]) => void,
  ): void;
}
```

---

## UI Affordance Types

Types for defining and working with UI affordances. Most types are generic over slot type `S` for custom slot support.

### Slots

```ts
// Default slots for backwards compatibility
type DefaultSlots = "primaryCta" | "secondaryCta" | "widgetVariant";

// Alias for backwards compatibility (deprecated, use DefaultSlots)
type CanonicalSlot = DefaultSlots;
```

### Bindings

```ts
// Normalized binding - the selection condition for a handle
type Binding = {
  key: string; // Decision field to match (e.g., "nextTool")
  value: string; // Value to match
};

// DX-friendly input format for registering handles
// { toolName } is shorthand for { key: "nextTool", value: toolName }
type BindingInput = { toolName: string } | { key: string; value: string };
```

### Handles and Registrations

```ts
// A registered UI affordance handle (generic over slot type)
type HandleRegistration<S extends string = DefaultSlots> = {
  slot: S;
  handleId: string;
  bindsTo: Binding;
};

// Collection of registered handles (generic over slot type)
type AffordanceRegistry<S extends string = DefaultSlots> = {
  handles: HandleRegistration<S>[];
};
```

### Selections

```ts
// A selected handle for a slot
type Selection = {
  handleId: string;
  bindsTo: Binding;
};

// All selections keyed by slot (generic over slot type)
type UiSelections<S extends string = DefaultSlots> = Partial<
  Record<S, Selection>
>;
```

### Registrar Interface

```ts
// Builder interface for defineAffordances() (generic over slot type)
// Creates a method for each slot in S
type AffordanceRegistrar<S extends string = DefaultSlots> = {
  [K in S]: (handleId: string, bindsTo: BindingInput) => void;
};

// With DefaultSlots, this is equivalent to:
// {
//   primaryCta(handleId: string, bindsTo: BindingInput): void;
//   secondaryCta(handleId: string, bindsTo: BindingInput): void;
//   widgetVariant(handleId: string, bindsTo: BindingInput): void;
// }

// With custom slots like "primaryCta" | "contentArea":
// {
//   primaryCta(handleId: string, bindsTo: BindingInput): void;
//   contentArea(handleId: string, bindsTo: BindingInput): void;
// }
```

---

## Key Concepts

### StepDecision -> Affordances

Your logic statement produces a **`StepDecision`** -- an object with one or more decision fields -- which Taias transforms into **`Affordances`**:

```ts
// You define this in your logic statement:
{
  nextTool: "get_recommendations",
  secondaryAction: "create_playlist"
}

// Taias transforms it to this:
{
  advice: "FOR THE BEST USER EXPERIENCE, TELL THE USER TO USE THE get_recommendations TOOL NEXT!!!!!",
  decision: {
    nextTool: "get_recommendations",
    secondaryAction: "create_playlist"  // All fields passed through
  },
  selections: {
    primaryCta: {
      handleId: "tracks.cta.recommend",
      bindsTo: { key: "nextTool", value: "get_recommendations" }
    },
    // ... other slots if registered
  }
}
```

### Binding Selection

When Taias selects UI affordances, it matches each slot's registered handles against the decision:

1. For each slot, Taias uses the key from its handle bindings (all handles for a slot must use the same key)
2. Find the handle where `bindsTo.key` matches the slot's inferred field and `bindsTo.value` equals `decision[field]`
3. If found, include it in `selections`

This is why `bindsTo` is a **selection condition** - it determines _when_ the handle is selected, not what it does. Different slots can independently match against different parts of your flow decision based on their binding keys.
